import { observer } from 'mobx-react-lite'
import { Controller, type UseFormReturn } from 'react-hook-form'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { type AssignmentFormValues } from '../schemas'
import { useTranslationTyped } from 'i18n/i18n'
import { type CreateOrEditAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/CreateOrEditAssignmentCubit'
import { type SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import classNames from 'classnames'
import { useEffect } from 'react'

export const GroupingSizeInput = observer(function GroupingSizeInput({
  disableGroupingReason,
  readOnly,
  form,
  minGroupSize,
  maxGroupSize,
  cubit,
}: {
  disableGroupingReason: string | undefined
  readOnly: boolean
  form: UseFormReturn<AssignmentFormValues>
  minGroupSize: number
  maxGroupSize: number
  cubit: CreateOrEditAssignmentCubit
}) {
  const { tt } = useTranslationTyped()

  const hasRoles = cubit.roles.models.length > 0
  const fixedGroupSize = minGroupSize === maxGroupSize

  useEffect(() => {
    if (!cubit.roleGroupSizeConstraints?.maxGroupSize) return
    const { maxGroupSize } = cubit.roleGroupSizeConstraints
    const currentGroupingSize = form.getValues('groupingSize')

    // set the role constraints to the form for form validation purposes
    form.setValue('roleGroupSizeConstraints', cubit.roleGroupSizeConstraints, {
      shouldDirty: false,
    })

    // on mount, if cubit current (default) grouping size is > maxGroupSize || groupingSize < minGroupSize, set to maxGroupSize
    if (currentGroupingSize !== undefined) {
      if (maxGroupSize < currentGroupingSize)
        form.setValue('groupingSize', maxGroupSize, { shouldDirty: false })
      else if (currentGroupingSize < minGroupSize)
        form.setValue('groupingSize', minGroupSize, { shouldDirty: false })
    }

    form.trigger('groupingSize')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // if no roles use simple input
  if (!hasRoles)
    return (
      <Controller
        control={form.control}
        name="groupingSize"
        disabled={!!disableGroupingReason || readOnly}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <BreakoutTextInput
              {...field}
              kind="secondary"
              error={fieldState.error}
              type="number"
              onChange={(e) => {
                const value = parseInt(e.target.value, 10)
                field.onChange(isNaN(value) ? '' : value)
              }}
              label={tt.instructor_library.max_number_of_students()}
              name="groupingSize"
              min={minGroupSize}
              max={maxGroupSize}
              required
            />
            <strong className="pl-4 text-body-small text-on-surface-disabled">
              {tt.instructor_library.max_number_of_students_description()}
            </strong>
          </div>
        )}
      />
    )

  if (!hasRoles)
    return (
      <RolesDescriptionBox
        minGroupSize={minGroupSize}
        maxGroupSize={maxGroupSize}
        roles={cubit.roles.models}
      />
    )

  return (
    <div className="flex w-full flex-row items-center gap-5">
      {!fixedGroupSize && (
        <Controller
          control={form.control}
          name="groupingSize"
          disabled={!!disableGroupingReason || readOnly || fixedGroupSize}
          render={({ field, fieldState }) => {
            return (
              <BreakoutTextInput
                {...field}
                kind="secondary"
                className={classNames('flex-1')}
                error={fieldState.error}
                type="number"
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10)
                  field.onChange(isNaN(value) ? '' : value)
                }}
                label={tt.instructor_library.max_number_of_students()}
                name="groupingSize"
                min={minGroupSize}
                max={maxGroupSize}
                required
              />
            )
          }}
        />
      )}
      <RolesDescriptionBox
        minGroupSize={minGroupSize}
        maxGroupSize={maxGroupSize}
        roles={cubit.roles.models}
      />
    </div>
  )
})

const RolesDescriptionBox = ({
  minGroupSize,
  maxGroupSize,
  roles,
}: {
  minGroupSize: number
  maxGroupSize: number
  roles: SlideDeckRole[]
}) => {
  const { tt } = useTranslationTyped()
  const fixedGroupSize = minGroupSize === maxGroupSize

  return (
    <div className="flex flex-1 flex-col gap-3 rounded-lg border border-fixed-accent-color bg-fixed-eggshell px-3 py-2 text-body-large">
      <div>
        <h3 className="font-bold">
          {tt.instructor_library.roleplay_requirements()}
        </h3>
        <ul>
          {roles.map((role) => {
            const roleReqDisplay =
              role.data.roleMin === role.data.roleMax
                ? role.data.roleMin
                : `${role.data.roleMin} - ${role.data.roleMax}`

            return (
              <li key={role.id}>
                {tt.instructor_library.role_min_x_name({
                  roleMin: roleReqDisplay,
                  roleName: role.data.roleName,
                })}
              </li>
            )
          })}
        </ul>
      </div>
      <span>
        {fixedGroupSize &&
          tt.instructor_library.role_fixed_group_size_description({
            groupSize: minGroupSize,
          })}
        {!fixedGroupSize &&
          tt.instructor_library.role_range_group_size_description({
            minGroupSize,
            maxGroupSize,
          })}
      </span>
    </div>
  )
}
